import { useState } from 'react';
import { Modal, ModalBody, type PlumaManagedModalProps } from '@customerio/pluma-components/react';
import { useBuilderSendRate } from '@/features/billing-usage';
import { BUILDER_CREDITS_CONFIG } from '../utils/credits-purchase-config';
import type { CreditsPurchasePageName } from '../utils/credits-purchase-page-name';
import { CreditsPurchaseForm } from './credits-purchase-form';

export type BuilderCreditsPurchaseModalData = {
  /** Current Builder balance in cents; caps the purchasable amount at the account balance limit. */
  currentBalanceCents: number;
  /** The surface that opened the modal, reported with every event the form fires. */
  pageName: CreditsPurchasePageName;
  /**
   * Dollars to open the amount field on, for a preset top-up. Builder funds are priced at a dollar a
   * unit, so the amount is the quantity. Defaults to the config amount. An amount the balance has no
   * room for opens as asked, and the amount field states the cap.
   */
  initialAmountDollars?: number;
};

export type BuilderCreditsPurchaseResult = { savedCard: boolean };

/**
 * Managed modal for adding to the Builder account balance. Hosts the Stripe credit-purchase form
 * with the Builder config, confirming with the purchase result so the opener can refresh balances.
 * While a purchase is processing the modal is non-dismissable.
 */
export function BuilderCreditsPurchaseModal({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<BuilderCreditsPurchaseModalData, BuilderCreditsPurchaseResult>) {
  const [isProcessing, setIsProcessing] = useState(false);
  // Read here rather than taken from the opener: a rate passed in on open would stay the fallback
  // for the life of a modal opened before the usage read landed.
  const { costPerMessage } = useBuilderSendRate();

  return (
    <Modal
      data-test-builder-credits-purchase-modal
      state={modalState}
      title="Add to account balance"
      isDismissable={!isProcessing}
    >
      <ModalBody>
        <CreditsPurchaseForm
          config={BUILDER_CREDITS_CONFIG}
          pageName={data.pageName}
          currentBalanceCents={data.currentBalanceCents}
          initialQuantity={data.initialAmountDollars}
          costPerMessage={costPerMessage}
          onSuccess={onConfirm}
          onCancel={onClose}
          onProcessingChange={setIsProcessing}
        />
      </ModalBody>
    </Modal>
  );
}
